import moment from 'moment/moment';
import { apptIssueType, formatAMPMNeW, formatIssueType, Images, noteStatusEnums, PermissionList, RolePermission, User } from '../../../../../utils';
import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { billStatuses, DRIVE, isUnassignedAppt, scheduleStatuses, SERVICE, UNAVAILABLE } from '../../../constants';
import { DateRow, EditWithIcon, LinkRow, SimpleTooltip, TextRow } from '../../../../../components';
import { ApptNoteActions } from '../../../../client/clientSingle/core/notes/apptNoteActions';

/**
 * Badge for a service appt that has no staff yet (Figma: Orange badge + "user" icon).
 * Default (md) in the Staff column / Staff row; size="sm" on the calendar card.
 */
export const UnassignedStaffChip = ({ size }) => (
    <div className={`appt-status-card appt-unassigned appt-unassigned-staff${size === 'sm' ? ' appt-unassigned-sm' : ''}`}>
        <svg className="appt-unassigned-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
                d="M16.667 17.5v-1.667a3.333 3.333 0 0 0-3.333-3.333H6.667a3.333 3.333 0 0 0-3.333 3.333V17.5m10-11.667a3.333 3.333 0 1 1-6.667 0 3.333 3.333 0 0 1 6.667 0Z"
                stroke="currentColor"
                strokeWidth="1.667"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
        Unassigned
    </div>
);

export const apptDetailStatus = (status, item, fromAppt) => {
    if (isUnassignedAppt(item)) {
        return (
            <div className="appt-status-card appt-unassigned">
                <div />
                Unassigned
            </div>
        );
    }
    if (item?.type === UNAVAILABLE) {
        return (
            <div>
                <div className="appt-status-card unavailable-appt-completed">
                    <CheckIcon className="appt-status-check" />
                    Completed
                </div>
            </div>
        );
    } else {
        switch (status) {
            case 'CANCELLED':
                return (
                    <div className="flex-align-center" style={{ gap: '8px' }}>
                        <div
                            className={`appt-status-card ${
                                item?.type === SERVICE
                                    ? 'service-appt-cancelled'
                                    : item?.type === DRIVE
                                      ? 'drive-appt-cancelled'
                                      : 'staff-appt-cancelled'
                            }`}
                        >
                            <CloseIcon className="appt-status-check" />
                            Cancelled
                        </div>
                    </div>
                );
            case 'PENDING':
                return (
                    <div
                        className={`appt-status-card ${
                            item?.type === SERVICE ? 'service-appt-pending' : item?.type === DRIVE ? 'drive-appt-pending' : 'appt-pending'
                        }`}
                    >
                        <div />
                        Pending
                    </div>
                );
            case 'COMPLETED':
                return item?.type === SERVICE ? (
                    fromAppt ? (
                        <div>
                            <div className="appt-status-card service-appt-completed">
                                <CheckIcon className="appt-status-check" />
                                Completed
                            </div>
                            <p className="appt-bill-status">
                                {item?.billStatus === billStatuses.NOT_BILLED
                                    ? `(Not Billed)`
                                    : item?.billStatus === billStatuses.BILLED
                                      ? `(Billed)`
                                      : ''}
                            </p>
                        </div>
                    ) : (
                        <div className="appt-status-card service-appt-completed">
                            <CheckIcon className="appt-status-check" />
                            {`Completed ${item?.billStatus === billStatuses.NOT_BILLED ? `(Not Billed)` : item?.billStatus === billStatuses.BILLED ? `(Billed)` : ''}`}
                        </div>
                    )
                ) : item?.type === DRIVE ? (
                    <div className="appt-status-card drive-appt-completed">
                        <CheckIcon className="appt-status-check" />
                        Completed
                    </div>
                ) : (
                    <div className="appt-status-card appt-completed">
                        <CheckIcon className="appt-status-check" />
                        Completed
                    </div>
                );
            default:
                // Rendered as a React child, so it must not be a plain object.
                return null;
        }
    }
};

export const serviceApptDetailList = (item) => [
    {
        title: 'Scheduled D/T:',
        value: (
            <div className="flex-align-center" style={{ gap: '8px' }}>
                {item?.isActualTimeSet ? (
                    <p className="schedule-time">{item?.scheduleDateTime}</p>
                ) : (
                    <>
                        <p>{moment.utc(item?.startDate).format('MM/DD/YYYY')}</p>
                        <p className="schedule-time">{`${formatAMPMNeW(item?.startTime)} - ${formatAMPMNeW(item?.endTime)}`}</p>
                    </>
                )}
            </div>
        ),
    },
    {
        title: 'Actual Time:',
        value:
            item?.status !== scheduleStatuses?.PENDING ? (
                <div className="flex-align-center" style={{ gap: '8px' }}>
                    <p className="schedule-time">{`${formatAMPMNeW(item?.startTime)} - ${formatAMPMNeW(item?.endTime)}`}</p>
                </div>
            ) : null,
    },
    {
        title: 'Staff:',
        link: true,
        value: item?.staff ? (
            <LinkRow
                staff={item?.staff}
                href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staff?.id}` : ''}
                checkPermission={item?.staff?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                defaultColor={'#172B4D'}
            />
        ) : isUnassignedAppt(item) ? (
            <UnassignedStaffChip />
        ) : null,
    },
    {
        title: 'Supervised:',
        link: true,
        value: item?.supervisee ? (
            <LinkRow
                staff={item?.supervisee}
                href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.supervisee?.id}` : ''}
                checkPermission={item?.supervisee?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                defaultColor={'#172B4D'}
            />
        ) : null,
    },
    {
        title: 'Client:',
        link: true,
        value: item?.client ? (
            <LinkRow
                client={item?.client}
                href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.client?.id || item?.client?._id}` : ''}
                checkPermission={item?.client ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                defaultColor={'#172B4D'}
            />
        ) : null,
    },
    {
        title: 'Service:',
        value: `${item?.authorizedService?.service?.cptCode} ${item?.chargeRateName ? `(${item.chargeRateName})` : ''}`,
    },
];
export const apptDetailList = (item) => [
    // { title: 'Appt ID:', value: item?.displayId },
    {
        title: 'Date/Time:',
        value: (
            <div className="flex-align-center" style={{ gap: '8px' }}>
                <p>{moment.utc(item?.startDate).format('MM/DD/YYYY')}</p>
                <p className="schedule-time">{`${formatAMPMNeW(item?.startTime)} - ${formatAMPMNeW(item?.endTime)}`}</p>
            </div>
        ),
    },
    {
        title: 'Staff:',
        value: item?.staff ? (
            <LinkRow
                staff={item?.staff}
                href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staff?.id}` : ''}
                checkPermission={item?.staff?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                defaultColor={'#172B4D'}
            />
        ) : null,
        link: true,
    },
];

export const reasonEnums = {
    STAFF_CANCELLATION: 'STAFF_CANCELLATION',
    CLIENT_CANCELLATION: 'CLIENT_CANCELLATION',
    CLIENT_LATE: 'CLIENT_LATE',
    OTHER: 'OTHER',
};

export const reasonEnumsConvert = {
    STAFF_CANCELLATION: 'Staff Cancellation',
    CLIENT_CANCELLATION: 'Client Cancellation',
    CLIENT_LATE: 'Client Late',
    OTHER: 'Other',
};

export const reasonList = [
    { title: 'Staff Cancellation', value: reasonEnums.STAFF_CANCELLATION },
    { title: 'Client Cancellation', value: reasonEnums.CLIENT_CANCELLATION },
    { title: 'Client Late', value: reasonEnums.CLIENT_LATE },
    { title: 'Other', value: reasonEnums.OTHER },
];

/** @param description the issue's saved text — used where it says more than the generic line (which codes the service accepts) */
export const renderApptStateIssues = (type, description) => {
    switch (type) {
        case apptIssueType.INCOMPLETE_UNITS:
            return 'Incomplete units.';
        case apptIssueType.EXCEEDED_UNITS:
            return 'Exceeded units.';
        case apptIssueType.AUTHORIZATION_EXPIRED:
            return 'Authorization expired.';
        case apptIssueType.UNIT_OUT_OF_RANGE:
            return 'Unit out of range.';
        case apptIssueType.FREQUENCY_LIMIT_EXCEEDED:
            return 'Frequency limit exceeded.';
        case apptIssueType.TB_DOJ_ERROR:
            return 'TB/DOJ credential has issues (unchecked or expired).';
        case apptIssueType.DIAGNOSTIC_CODE_MISSING:
            return 'Missing diagnostic code.';
        case apptIssueType.DIAGNOSIS_MISMATCH:
            return description || 'Diagnosis mismatch: the client has no diagnosis accepted by this service.';

        default:
            return formatIssueType(type);
    }
};

export const noteEnums = {
    OFFICE_NOTES: 'OFFICE_NOTES',
    CLINICAL_NOTES: 'CLINICAL_NOTES',
    // ABC Data notes ride the same appt-note endpoints as the two above but carry only a
    // Draft/Completed label — no review workflow. Deliberately absent from `noteTypes` below:
    // they are authored from the session Documentation modal, not the appt Notes filter.
    ABC_DATA: 'ABC_DATA',
};
export const noteTypes = [
    { name: 'ALL', id: 'ALL' },
    { name: 'Office Notes', id: noteEnums.OFFICE_NOTES },
    { name: 'Session Notes', id: noteEnums.CLINICAL_NOTES },
    // { name: 'Clinical Notes', id: noteEnums.CLINICAL_NOTES },
];

export const apptNotesHead = [
    { name: 'subject', title: 'Form Title', custom: false, noSearch: true },
    { name: 'description', title: 'Form Description', custom: false, noSearch: true },
    { name: 'date', title: 'Created Date', icon: 'date', disabled: true, custom: false, noSearch: true },
    { name: 'user', rowName: 'firstName', title: 'Creator Name', custom: false, noSearch: true },
    { name: 'action', title: 'Action', custom: false, disabled: true, noSearch: true, width: '200px' },
];

export const apptNotesBody = (appt, serviceTemplate, noteType) => {
    return [
        { rowText: (item) => <TextRow name={item?.template?.title} /> },
        { rowText: (item) => <TextRow name={item?.template?.description} /> },
        { rowText: (item) => <DateRow date={item?.createdDate} /> },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.creatorFullName ? item?.creatorFullName : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.creatorId}` : ''}
                    checkPermission={item?.creatorFullName ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                    textWidth={8}
                />
            ),
            notClickable: true,
        },
        {
            button: (fn, item) => (
                <>
                    <ApptNoteActions
                        apptId={appt?.id}
                        item={item}
                        appt={appt}
                        serviceTemplate={serviceTemplate}
                        noteType={noteType}
                        single={true}
                    />
                </>
            ),
            notClickable: true,
        },
    ];
};

const noteStatusConfig = {
    [noteStatusEnums.DRAFT]: { label: 'Draft', background: '#F2F4F7', color: '#344054', icon: null },
    [noteStatusEnums.COMPLETED]: { label: 'Completed', background: '#ECFDF3', color: '#027A48', icon: Images.lockSmallGreen },
    [noteStatusEnums.IN_REVIEW]: { label: 'In Review', background: '#EFF8FF', color: '#175CD3', icon: Images.showAyeBlue },
    [noteStatusEnums.UPDATE_REQUEST]: { label: 'Update Req.', background: '#FFFAEB', color: '#B54708', icon: null },
};

export const NoteStatusBadge = ({ status, doNotShowIcon }) => {
    const config = noteStatusConfig[status];
    if (!config) return <span>—</span>;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: '18px',
                whiteSpace: 'nowrap',
                background: config.background,
                color: config.color,
            }}>
            {/*{config.icon && <span style={{ fontSize: '10px' }}>{config.icon}</span>}*/}
            {config.icon && !doNotShowIcon && <img style={{ width: 12, height: 12 }} src={config.icon} alt="icon" />}
            {config.label}
        </span>
    );
};

export const apptSessionNotesHead = [
    { name: 'subject', title: 'Form Title', custom: false, noSearch: true },
    { name: 'description', title: 'Form Description', custom: false, noSearch: true },
    { name: 'date', title: 'Created Date', icon: 'date', disabled: true, custom: false, noSearch: true },
    { name: 'user', rowName: 'firstName', title: 'Creator Name', custom: false, noSearch: true },
    { name: 'status', title: 'Status', custom: false, disabled: true, noSearch: true, width: '140px' },
    { name: 'action', title: 'Action', custom: false, disabled: true, noSearch: true, width: '200px' },
];

export const apptSessionNotesBody = (appt, serviceTemplate) => {

    return [
        { rowText: (item) => <TextRow name={item?.template?.title} /> },
        { rowText: (item) => <TextRow name={item?.template?.description} /> },
        { rowText: (item) => <DateRow date={item?.createdDate} /> },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.creatorFullName ? item?.creatorFullName : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.creatorId}` : ''}
                    checkPermission={item?.creatorFullName ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                    textWidth={8}
                />
            ),
            notClickable: true,
        },
        {
            rowText: (item) => <NoteStatusBadge status={item?.status} />,
        },
        {
            button: (fn, item) => (
                <>
                    <ApptNoteActions
                        noteType={item?.type}
                        apptId={appt?.id}
                        item={item}
                        appt={appt}
                        serviceTemplate={serviceTemplate}
                        single={true}
                    />
                </>
            ),
            notClickable: true,
        },
    ];
};

export const linksNotYet = {
    title: 'No Links Yet',
    subTitle: 'Add relevant links for this appointment.',
    image: Images.noService,
    height: '300px',
};

export const noteNotYet = {
    title: 'No Notes Yet',
    subTitle: 'Keep track of important details here.',
    image: Images.noService,
    height: '300px',
};

export const sessionNoteNotYet = {
    title: 'No Session Notes Yet',
    subTitle: 'Keep track of important details here.',
    image: Images.noService,
    height: '300px',
};

export const noDocumentsYet = {
    title: 'No Documents Yet',
    subTitle: 'Upload documents or images here.',
    image: Images.noService,
    height: '300px',
};

export const noHistoryYet = {
    title: 'No History Yet',
    subTitle: 'All updates will appear here.',
    image: Images.noService,
    height: '342px',
};

export const noIssueYet = {
    title: 'No Issues Yet',
    subTitle: 'Any issues will show up here.',
    image: Images.emptyStateIllustrations,
    height: '360px',
};

export const completeApptTabEnums = {
    SIGNATURE: 'SIGNATURE',
    NOTES: 'NOTES',
};

export const expandedSignatureEnums = {
    PROVIDER: 'provider',
    CLIENT: 'client',
};
export const completeApptTab = (fromSession) => [
    { title: 'Signature', tabValue: completeApptTabEnums.SIGNATURE },
    { title: 'Session Notes', tabValue: completeApptTabEnums.NOTES },
    // { title: fromSession ? 'Session Notes' : 'Notes', tabValue: completeApptTabEnums.NOTES },
];

export const tabEnums = {
    DRAW: 'Draw',
    UPLOAD: 'Upload',
};

export const roundUnitEnums = {
    ROUND_UP: 'ROUND_UP',
    ROUND_DOWN: 'ROUND_DOWN',
    ROUND_REVERT: 'ROUND_REVERT',
};
